/* eslint-disable @next/next/no-css-tags */
import '@tamagui/core/reset.css'
import '@tamagui/polyfill-dev'
import 'raf/polyfill'
import 'react-medium-image-zoom/dist/styles.css'
import '../styles/index.css'

import ProgressBar from '@badrap/bar-of-progress'
import { Provider } from 'app/provider'
import localFont from 'next/font/local'
import Head from 'next/head'
import Script from 'next/script'
import { useEffect } from 'react'
import type { SolitoAppProps } from 'solito'

import { config as tamaguiConfig } from '@mythical/ui'
import { NextThemeProvider } from '@tamagui/next-theme'
import { useScrollRestoration } from 'app/hooks/use-scroll-restoration'
import * as fbq from 'app/lib/fbpixel'
import { useRouter } from 'next/router'
import GlobalMeta from '../src/Meta'

const progress = new ProgressBar({
  size: 2,
  color: '#7750ED', // Brand purple
  className: 'bar-of-progress',
  delay: 150,
})

const regular = localFont({
  src: [
    { path: '../assets/fonts/Obviously-Regular.woff2', weight: '400' },
    { path: '../assets/fonts/Obviously-Medium.woff2', weight: '500' },
    { path: '../assets/fonts/Obviously-Semibold.woff2', weight: '600' },
    { path: '../assets/fonts/Obviously-Bold.woff2', weight: '700' },
    { path: '../assets/fonts/Obviously-Black.woff2', weight: '800' },
  ],
})

const narrow = localFont({
  src: [
    { path: '../assets/fonts/Obviously-NarrowBold.woff2', weight: '700' },
    { path: '../assets/fonts/Obviously-NarrowBlack.woff2', weight: '800' },
  ],
})

const condensed = localFont({
  src: [
    { path: '../assets/fonts/Obviously-CondensedBold.woff2', weight: '700' },
    { path: '../assets/fonts/Obviously-CondensedBlack.woff2', weight: '800' },
  ],
})

const extended = localFont({
  src: [{ path: '../assets/fonts/Obviously-ExtendedBold.woff2', weight: '700' }],
})

const wide = localFont({
  src: [{ path: '../assets/fonts/Obviously-WideBold.woff2', weight: '700' }],
})

function MyApp({
  Component,
  pageProps,
}: SolitoAppProps<{ dehydratedState: any }>) {
  const router = useRouter()

  useScrollRestoration()

  useEffect(() => {
    const handleRouteChange = function () {
      progress.finish()
      fbq.pageview()
    }

    router.events.on('routeChangeStart', progress.start)
    router.events.on('routeChangeComplete', handleRouteChange)
    router.events.on('routeChangeError', progress.finish)
    fbq.pageview()

    return () => {
      router.events.off('routeChangeStart', progress.start)
      router.events.off('routeChangeComplete', handleRouteChange)
      router.events.off('routeChangeError', progress.finish)
    }
  }, [router])

  return (
    <>
      <GlobalMeta />
      <Head>
        {process.env.NODE_ENV === 'production' && (
          <link rel="stylesheet" href="/tamagui.css" />
        )}
        <style
          dangerouslySetInnerHTML={{
            // the first time this runs you'll get the full CSS including all themes
            // after that, it will only return CSS generated since the last call
            __html: tamaguiConfig.getNewCSS(),
          }}
        />

        <style
          dangerouslySetInnerHTML={{
            __html: tamaguiConfig.getCSS({
              // if you are using "outputCSS" option, you should use this "exclude"
              // if not, then you can leave the option out
              exclude:
                process.env.NODE_ENV === 'production' ? 'design-system' : null,
            }),
          }}
        />
        <Script
          dangerouslySetInnerHTML={{
            // avoid flash of animated things on enter:
            __html: `document.documentElement.classList.add('t_unmounted')`,
          }}
        />
      </Head>
      <style jsx global>{`
        :root {
          --font-obviously-regular: ${regular.style.fontFamily};
          --font-obviously-narrow: ${narrow.style.fontFamily};
          --font-obviously-condensed: ${condensed.style.fontFamily};
          --font-obviously-extended: ${extended.style.fontFamily};
          --font-obviously-wide: ${wide.style.fontFamily};
        }
      `}</style>

      <Script id="cookieyes" type="text/javascript" src="https://cdn-cookieyes.com/client_data/1f5fc8f9e5e71aeecfc58ccc7e23b487/script.js" />

      <Script
        id="accessibe"
        strategy="afterInteractive"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `(function(){
          var s    = document.createElement('script');
          var h    = document.querySelector('head') || document.body;
          s.src    = 'https://acsbapp.com/apps/app/dist/js/app.js';
          s.async  = true;
          s.onload = function(){
              acsbJS.init({
                  statementLink    : '',
                  footerHtml       : '',
                  hideMobile       : false,
                  hideTrigger      : false,
                  disableBgProcess : false,
                  language         : 'en',
                  position         : 'right',
                  leadColor        : '#2a3314',
                  triggerColor     : '#7750ED',
                  triggerRadius    : '50%',
                  triggerPositionX : 'right',
                  triggerPositionY : 'bottom',
                  triggerIcon      : 'people',
                  triggerSize      : 'bottom',
                  triggerOffsetX   : 20,
                  triggerOffsetY   : 20,
                  mobile           : {
                      triggerSize      : 'small',
                      triggerPositionX : 'right',
                      triggerPositionY : 'bottom',
                      triggerOffsetX   : 10,
                      triggerOffsetY   : 10,
                      triggerRadius    : '20'
                  }
              });
          };
          h.appendChild(s);
        })();`,
        }}
      />

      <Script
        id="fb-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', ${fbq.FB_PIXEL_ID});
          `,
        }}
      />

      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-KT24Z6J6');
      `}
      </Script>

      <NextThemeProvider skipNextHead defaultTheme="light">
        <Provider
          disableRootThemeClass
          defaultTheme="light"
          queryState={pageProps.dehydratedState}
          initialUserStore={pageProps.initialUserStore}
        >
          <Component {...pageProps} />
        </Provider>
      </NextThemeProvider>
    </>
  )
}

MyApp.displayName = 'MythicalApp'

export default MyApp
